/**
 * Process Thread Execution API Client
 * Handles execution history for a workflow (ProcessThread) — backed by
 * BaseProcessThreadExecutionController / the ProcessThreadExecution entity.
 */

import { BaseApiClient, type ApiResponse, RequestOptions } from './baseApiClient';
import type { ExecutionHistoryItem } from '@flow-studio/types/execution.types';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import { normalizeServerDate } from '../utils/serverDate';
import { processExecutionApiClient } from './processExecutionApiClient';

// ============================================================================
// WIRE SHAPE (raw ProcessThreadExecution entity, PascalCase as sent by the backend)
// ============================================================================

interface ProcessThreadExecutionDto {
  ProcessThreadExecutionID: number;
  ProcessExecutionID?: number | null;
  ProcessThreadID: number;
  ExecutionModeID: number;
  ExecutionStatusID: number;
  StartedAt: string;
  StoppedAt?: string | null;
  Duration?: number | null;
  ErrorMessage?: string | null;
  TotalNodes?: number | null;
  CompletedNodes?: number | null;
  FailedNodes?: number | null;
  SkippedNodes?: number | null;
}

function mapDtoToHistoryItem(dto: ProcessThreadExecutionDto): ExecutionHistoryItem {
  return {
    processThreadExecutionId: dto.ProcessThreadExecutionID,
    processExecutionId: dto.ProcessExecutionID ?? undefined,
    processThreadId: dto.ProcessThreadID,
    executionModeId: dto.ExecutionModeID,
    executionStatusId: dto.ExecutionStatusID,
    // StartedAt/StoppedAt arrive as UTC digits with no offset marker — tag them so
    // consumers' new Date() doesn't reparse them as local time. See normalizeServerDate.
    startedAt: normalizeServerDate(dto.StartedAt),
    stoppedAt: normalizeServerDate(dto.StoppedAt),
    duration: dto.Duration ?? undefined,
    errorMessage: dto.ErrorMessage ?? undefined,
    totalNodes: dto.TotalNodes ?? undefined,
    completedNodes: dto.CompletedNodes ?? undefined,
    failedNodes: dto.FailedNodes ?? undefined,
    skippedNodes: dto.SkippedNodes ?? undefined,
  };
}

/**
 * ProcessThreadExecution's own completion fields are never updated after creation (see
 * PROCESS_EXECUTION endpoint comment in apiEndpoints.ts) — only the parent ProcessExecution
 * row is. Enrich each history row with the real data from there. Not batched (one request
 * per row with a processExecutionId, run in parallel) — acceptable for a page-sized list,
 * but a backend endpoint that joins these server-side would be more efficient.
 */
async function enrichWithRealCompletionData(item: ExecutionHistoryItem): Promise<ExecutionHistoryItem> {
  if (item.processExecutionId == null) return item;
  try {
    const response = await processExecutionApiClient.getById(item.processExecutionId);
    if (!response.success || !response.data) return item;
    const info = response.data;
    return {
      ...item,
      executionStatusId: info.executionStatusId,
      stoppedAt: info.stoppedAt,
      duration: info.duration,
      totalNodes: info.totalNodes,
      completedNodes: info.completedNodes,
      failedNodes: info.failedNodes,
      skippedNodes: info.skippedNodes,
      errorMessage: info.errorMessage ?? item.errorMessage,
    };
  } catch {
    return item;
  }
}

// ============================================================================
// REQUEST TYPES
// ============================================================================

export interface ProcessThreadExecutionSearchParams {
  processThreadId: number;
  pageNumber?: number;
  pageSize?: number;
}

// ============================================================================
// API CLIENT
// ============================================================================

class ProcessThreadExecutionApiClient extends BaseApiClient {
  /**
   * List past executions for a workflow (ProcessThread), newest first —
   * POST api/v1/process/process-thread-executions/by-process-thread.
   */
  public async search(
    params: ProcessThreadExecutionSearchParams,
    options?: RequestOptions
  ): Promise<ApiResponse<ExecutionHistoryItem[]>> {
    const response = await this.postQuery<ProcessThreadExecutionDto[]>(
      API_ENDPOINTS.PROCESS_THREAD_EXECUTION.BY_PROCESS_THREAD,
      {
        ProcessThreadID: params.processThreadId,
        PageInfo: { PageNumber: params.pageNumber ?? 1, PageSize: params.pageSize ?? 20 },
      },
      options
    );
    if (!response.success || !response.data) {
      return response as unknown as ApiResponse<ExecutionHistoryItem[]>;
    }
    const items = await Promise.all(response.data.map(mapDtoToHistoryItem).map(enrichWithRealCompletionData));
    return { ...response, data: items };
  }

  /**
   * Get a single execution by its ProcessThreadExecutionID —
   * POST api/v1/process/process-thread-executions/by-id.
   */
  public async getById(
    processThreadExecutionId: number,
    options?: RequestOptions
  ): Promise<ApiResponse<ExecutionHistoryItem>> {
    // by-id needs ID/RaiseErrorIfNotFound spread at the request root, not nested under a
    // 'data' key — postQuery does that; post() nests, which left the backend seeing no ID
    // at all and 404ing. Confirmed against processThreadApiClient.getById's working pattern.
    const response = await this.postQuery<ProcessThreadExecutionDto>(
      API_ENDPOINTS.PROCESS_THREAD_EXECUTION.BY_ID,
      { ID: { ID: processThreadExecutionId, ResID: '' }, RaiseErrorIfNotFound: false },
      options
    );
    if (!response.success || !response.data) {
      return response as unknown as ApiResponse<ExecutionHistoryItem>;
    }
    const item = await enrichWithRealCompletionData(mapDtoToHistoryItem(response.data));
    return { ...response, data: item };
  }
}

export const processThreadExecutionApiClient = new ProcessThreadExecutionApiClient();
